import React, {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import DashboardSection from "../DashboardSection";
import SectionHeader from "../SectionHeader";
import UserContext, {
  isUserAdminOrSemiAdminWith,
  ServerOrgRoles,
} from "../../../services/UserContext";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { InputText } from "primereact/inputtext";
import CalendarRow, { useHeaderRow } from "./CalendarRow";
import GrowlContext from "../../../services/growlContext";
import {
  createAuthenticatedRequest,
  createRequestWithAuthHeaders,
  getFullUrl,
} from "../../../configs/axios-export.custom";
import axios from "axios";
import "./EventsCalendar.css";
import { format } from "date-fns";
import { ProfileResponse } from "../../../screens/UserDashboard";
import ShowMoreTextComp from "../../ShowMoreTextComp";
import "datejs";
import ReactDOM from "react-dom";
import SimpleModal from "../../Modals/SimpleModal";
import { Box, Button as MuiButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { useForm } from "react-hook-form";
import {
  createNewEvent,
  NewEventValues,
  resetCreateEventState,
  resetUpdateEventState,
} from "../../../slices/dashboard";
import Input from "../../Widgets/Inputs/Input";
import { RootState, useDispatch, useSelector } from "../../../store";
import { RequestStatus } from "../../../utils/Helpers/fetchStatus";
import toast from "react-hot-toast";
import {
  getISODateWithAdjustedTimezone,
  getNewDateNoTimezoneAdjustment,
} from "../../../utils/conversion/date-converters";
import AddEditEventModal from "./AddEditEventModal";

export interface EventType {
  // TODO: Event time? seems like it might be important. Can be part of Date depending on serialization
  id: number;
  name: string;
  date: string;
  dateAsString: string;
  organization: string;
  url: string;
  location: string;
  createdOn: string;
}

export const sharedStyles = {
  controls: {
    whiteSpace: "nowrap" as "nowrap",
  },
  iconButton: {
    width: "30px",
    margin: "5px",
  },
  inlineInput: {
    width: "100%",
  },
  cell: {
    verticalAlign: "top",
  },
};

const styles = {
  eventsTable: {
    margin: "20px 0",
    textAlign: "left" as "left",
    width: "100%",
    borderSpacing: "10px",
  },
};

type Props = { events: Array<EventType> };
const EventsCalendar: FunctionComponent<Props> = ({ events: inputEvents }) => {
  const nth = function (day: number) {
    if (day > 3 && day < 21) return "th";
    switch (day % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  const userContext = React.useContext(UserContext);
  const growl = React.useContext(GrowlContext);
  const [events, setEvents] = useState<Array<EventType>>([]);
  const [newEventDate, setNewEventDate] = useState<Date>(new Date());
  const [newEventName, setNewEventName] = useState<string>("");
  const [newEventOrganization, setNewEventOrganization] = useState<string>("");
  const [newEventLocation, setNewEventLocation] = useState<string>("");
  const [newEventUrl, setNewEventUrl] = useState<string>("");
  const [selectedView, setSelectedView] = useState<string>("list");
  const [selectedEvent, setSelectedEvent] = useState<EventType | null>(null);

  const eventPopupRef = useRef(null);
  const [bottomOffset, setBottomOffset] = useState<string>();

  const setEventFromInnerChild = (event: any, show: boolean, offset: any) => {
    if (show) {
      var element = ReactDOM.findDOMNode(eventPopupRef.current) as Element;
      if (element) {
        const elementRect = element.getBoundingClientRect();
        setBottomOffset(offset - elementRect.height + "px");
      }
    }
    setVisibleTooltip(show);
    if (show) setSelectedEvent(event);
  };
  const filterEvents = (date: Date) => {
    return events.filter((obj) => {
      return (
        new Date(obj.date).getDate() === date.getDate() &&
        new Date(obj.date).getFullYear() === date.getFullYear() &&
        new Date(obj.date).getMonth() === date.getMonth()
      );
    });
  };
  const convertDate = (date: Date) =>
    `${date.getFullYear()}-${
      date.getMonth() + 1 >= 10
        ? date.getMonth() + 1
        : "0" + (date.getMonth() + 1)
    }-${
      date.getDate() + 1 > 10 ? date.getDate() : "0" + date.getDate()
    }T11:00:00.000Z`;
  const changeSelectorColor = (selectedViewType: string) => {
    if (selectedViewType === "calendar") {
      return ["#ffffff", "#605F5F"];
    } else {
      return ["#605F5F", "#ffffff"];
    }
  };
  const selectedDateEvents = useMemo(
    () => filterEvents(newEventDate),
    [newEventDate, events],
  );
  const viewSelectorColors = useMemo(
    () => changeSelectorColor(selectedView),
    [selectedView],
  );
  const isNewEvent = (dateStr: string) => {
    var date = new Date(dateStr);
    var nowDate = new Date();
    nowDate.addDays(-7);
    return date >= nowDate;
  };
  const eventList = selectedDateEvents?.map((obj) => (
    <div className="event-box">
      <div className="event-title">
        <ShowMoreTextComp
          styles={{ width: "100%" }}
          text={obj.name}
          textLength={140}
        />
        {isNewEvent(obj.createdOn) ? (
          <span className="new-label">New</span>
        ) : (
          ""
        )}
      </div>
      <div>
        <span className="event-org">
          <ShowMoreTextComp text={obj.organization} textLength={140} />
        </span>
        <div style={{ display: "flex" }}>
          <div className="event-loc">
            <svg
              style={{ marginRight: "2px" }}
              width="11"
              height="13"
              viewBox="0 0 11 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.5 0.25C4.17392 0.25 2.90215 0.776784 1.96447 1.71447C1.02678 2.65215 0.5 3.92392 0.5 5.25C0.5 8.625 4.90625 12.4375 5.09375 12.6C5.20696 12.6968 5.35103 12.75 5.5 12.75C5.64897 12.75 5.79304 12.6968 5.90625 12.6C6.125 12.4375 10.5 8.625 10.5 5.25C10.5 3.92392 9.97322 2.65215 9.03553 1.71447C8.09785 0.776784 6.82608 0.25 5.5 0.25ZM5.5 11.2812C4.16875 10.0312 1.75 7.3375 1.75 5.25C1.75 4.25544 2.14509 3.30161 2.84835 2.59835C3.55161 1.89509 4.50544 1.5 5.5 1.5C6.49456 1.5 7.44839 1.89509 8.15165 2.59835C8.85491 3.30161 9.25 4.25544 9.25 5.25C9.25 7.3375 6.83125 10.0375 5.5 11.2812ZM5.5 2.75C5.00555 2.75 4.5222 2.89662 4.11107 3.17133C3.69995 3.44603 3.37952 3.83648 3.1903 4.29329C3.00108 4.75011 2.95157 5.25277 3.04804 5.73773C3.1445 6.22268 3.3826 6.66814 3.73223 7.01777C4.08186 7.3674 4.52732 7.6055 5.01227 7.70196C5.49723 7.79843 5.99989 7.74892 6.45671 7.5597C6.91352 7.37048 7.30397 7.05005 7.57867 6.63893C7.85338 6.2278 8 5.74445 8 5.25C8 4.58696 7.73661 3.95107 7.26777 3.48223C6.79893 3.01339 6.16304 2.75 5.5 2.75ZM5.5 6.5C5.25277 6.5 5.0111 6.42669 4.80554 6.28934C4.59998 6.15199 4.43976 5.95676 4.34515 5.72835C4.25054 5.49995 4.22579 5.24861 4.27402 5.00614C4.32225 4.76366 4.4413 4.54093 4.61612 4.36612C4.79093 4.1913 5.01366 4.07225 5.25614 4.02402C5.49861 3.97579 5.74995 4.00054 5.97835 4.09515C6.20676 4.18976 6.40199 4.34998 6.53934 4.55554C6.67669 4.7611 6.75 5.00277 6.75 5.25C6.75 5.58152 6.6183 5.89946 6.38388 6.13388C6.14946 6.3683 5.83152 6.5 5.5 6.5Z"
                fill="#666666"
              />
            </svg>
            <ShowMoreTextComp text={obj.location} textLength={140} />
          </div>
          <Button
            label="RSVP"
            icon="pi pi-external-link"
            className="cal-button"
            onClick={() => {
              window.open(obj.url, "_blank");
            }}
            disabled={!obj.url}
          />
        </div>
      </div>
    </div>
  ));

  function changeEventDate(date: Date) {
    setNewEventDate(date);
  }

  React.useEffect(() => {
    setEvents(inputEvents);
  }, [inputEvents]);

  React.useEffect(() => {
    setNewEventDate(newEventDate);
  }, [newEventDate]);

  const loadDataAsync = useCallback(() => {
    axios
      .get(
        getFullUrl("/api/user/profile", { useDedicatedEnvironment: true }),
        createRequestWithAuthHeaders(userContext),
      )
      .then((response) => {
        const { events }: ProfileResponse = response.data;
        setEvents(events);
      })
      .catch((e) => {
        console.error(e);
        if (growl.current) {
          growl.current.show({
            severity: "error",
            summary: "Unable to load user profile",
          });
        }
      });
  }, []);

  const addEvent = useCallback(
    async (
      name: string,
      date: Date,
      organization: string,
      location: string,
      url: string,
    ) => {
      const convertedDate = convertDate(date);
      await axios
        .post(
          getFullUrl("/api/user/event", { useDedicatedEnvironment: true }),
          {
            date: convertedDate,
            name,
            organization,
            location,
            url,
          },
          createRequestWithAuthHeaders(userContext),
        )
        .then(() => {
          loadDataAsync();
        })
        .catch((e) => {
          console.error(e);
          growl.current.show({
            severity: "error",
            summary: "Error saving new event",
            detail: e,
          });
        });
    },
    [],
  );

  const deleteEvent = useCallback(async (eventId: number) => {
    const request = {
      ...createRequestWithAuthHeaders(userContext),
      data: { id: eventId },
    };
    await axios
      .delete(
        getFullUrl("/api/user/event", { useDedicatedEnvironment: true }),
        request,
      )
      .then((e) => {
        axios
          .get(
            getFullUrl("/api/user/profile", { useDedicatedEnvironment: true }),
            createRequestWithAuthHeaders(userContext),
          )
          .then((response) => {
            const { events }: ProfileResponse = response.data;
            setEvents(events);
          })
          .catch((e) => {
            console.error(e);
            if (growl.current) {
              growl.current.show({
                severity: "error",
                summary: "Unable to load user profile",
              });
            }
          });
      })
      .catch((e) => {
        console.error(e);
        growl.current.show({
          severity: "error",
          summary: "Error deleting event",
          detail: JSON.stringify(e),
        });
      });
  }, []);

  const editEvent = useCallback(
    async (eventId: number, editedEvent: EventType, events: EventType[]) => {
      const date = new Date(editedEvent.dateAsString ?? editedEvent.date);
      const convertedDate = convertDate(date);
      await axios
        .put(
          getFullUrl("/api/user/event", { useDedicatedEnvironment: true }),
          {
            id: eventId,
            event: {
              date: convertedDate,
              name: editedEvent.name,
              organization: editedEvent.organization,
              location: editedEvent.location,
              url: editedEvent.url,
            },
          },
          createAuthenticatedRequest(userContext),
        )
        .then(() => {
          loadDataAsync();
        })
        .catch((e) => {
          console.error(e);
          growl.current.show({
            severity: "error",
            summary: "Error editing event",
            detail: e,
          });
        });
    },
    [],
  );

  function hideEventPopup() {
    setVisibleTooltip(false);
  }
  window.addEventListener("resize", hideEventPopup);
  window.addEventListener("scroll", hideEventPopup);

  const submitNewEvent = useCallback(
    async (
      newEventName,
      newEventDate,
      newEventOrganization,
      newEventLocation,
      newEventUrl,
    ) => {
      await addEvent(
        newEventName,
        newEventDate,
        newEventOrganization,
        newEventLocation,
        newEventUrl,
      );
      setNewEventDate(new Date());
      setNewEventName("");
      setNewEventOrganization("");
      setNewEventUrl("");
      setNewEventLocation("");
    },
    [],
  );

  const canEditEvents = isUserAdminOrSemiAdminWith(
    userContext,
    ServerOrgRoles.EditCalendarEvents,
  );

  const canSubmitNewEvent = useMemo(
    () =>
      newEventDate &&
      newEventName &&
      newEventOrganization &&
      newEventLocation &&
      newEventUrl,
    [
      newEventDate,
      newEventName,
      newEventOrganization,
      newEventLocation,
      newEventUrl,
    ],
  );
  const [visibleTooltip, setVisibleTooltip] = useState<boolean>(false);
  function renderView() {
    if (selectedView === "calendar")
      return (
        <div className="cal-container p-grid" style={{ paddingBottom: "14px" }}>
          <div className="calendar-block">
            <Calendar
              showOtherMonths={false}
              value={newEventDate}
              onChange={(e) => changeEventDate(e.value as Date)}
              inline
            ></Calendar>
          </div>
          <div className="env-con calendar-event-list">
            <div className="p-container">
              <h3>
                {selectedDateEvents.length === 1
                  ? " 1 Event available on "
                  : selectedDateEvents.length > 1
                  ? selectedDateEvents.length + " Events available on "
                  : "No Events available on "}{" "}
                <span className="month-title">
                  {format(newEventDate, "MMMM d") + nth(newEventDate.getDate())}
                </span>
              </h3>
            </div>
            {eventList}
          </div>
        </div>
      );
    else {
      return (
        <div className="env-con">
          <table style={styles.eventsTable}>
            <thead>{useHeaderRow()}</thead>
            <tbody>
              {events
                .sort(
                  (e1, e2) =>
                    new Date(e1.date).getTime() - new Date(e2.date).getTime(),
                )
                .map((event, i) => (
                  <CalendarRow
                    key={`${i}-${event.name}`}
                    event={event}
                    canEditEvents={canEditEvents}
                    editEvent={(id, event) => editEvent(id, event, events)}
                    deleteEvent={deleteEvent}
                    setEventToShow={setEventFromInnerChild}
                    eventOffset={i}
                    loadDataAsync={loadDataAsync}
                  />
                ))}
              {/*{canEditEvents && (*/}
              {/*  <tr>*/}
              {/*    <td>*/}
              {/*      <Calendar*/}
              {/*        appendTo={document.body}*/}
              {/*        placeholder="mm/dd/yyyy"*/}
              {/*        dateFormat="mm/dd/yy"*/}
              {/*        value={newEventDate}*/}
              {/*        onChange={(e) => setNewEventDate(e.value as Date)}*/}
              {/*      />*/}
              {/*    </td>*/}
              {/*    <td>*/}
              {/*      <InputText*/}
              {/*        placeholder="Event Name"*/}
              {/*        style={{*/}
              {/*          width: "49%",*/}
              {/*          marginRight: "5px",*/}
              {/*          marginBottom: "5px",*/}
              {/*        }}*/}
              {/*        value={newEventName}*/}
              {/*        onChange={(e) => setNewEventName(e.currentTarget.value)}*/}
              {/*      />*/}
              {/*      <InputText*/}
              {/*        placeholder="Location"*/}
              {/*        style={{ width: "49%" }}*/}
              {/*        value={newEventLocation}*/}
              {/*        onChange={(e) =>*/}
              {/*          setNewEventLocation(e.currentTarget.value)*/}
              {/*        }*/}
              {/*      />*/}
              {/*      <InputText*/}
              {/*        placeholder="RSVP Link"*/}
              {/*        style={sharedStyles.inlineInput}*/}
              {/*        value={newEventUrl}*/}
              {/*        onChange={(e) => setNewEventUrl(e.currentTarget.value)}*/}
              {/*      />*/}
              {/*    </td>*/}
              {/*    <td>*/}
              {/*      <InputText*/}
              {/*        placeholder="Organization"*/}
              {/*        style={sharedStyles.inlineInput}*/}
              {/*        value={newEventOrganization}*/}
              {/*        onChange={(e) =>*/}
              {/*          setNewEventOrganization(e.currentTarget.value)*/}
              {/*        }*/}
              {/*      />*/}
              {/*    </td>*/}
              {/*    <td*/}
              {/*      style={{ ...sharedStyles.cell, ...sharedStyles.controls }}*/}
              {/*    >*/}
              {/*      <Button*/}
              {/*        icon="pi pi-plus"*/}
              {/*        className="p-button-rounded aid-blu-btn"*/}
              {/*        style={sharedStyles.iconButton}*/}
              {/*        disabled={!canSubmitNewEvent}*/}
              {/*        onClick={() =>*/}
              {/*          submitNewEvent(*/}
              {/*            newEventName,*/}
              {/*            newEventDate,*/}
              {/*            newEventOrganization,*/}
              {/*            newEventLocation,*/}
              {/*            newEventUrl,*/}
              {/*          )*/}
              {/*        }*/}
              {/*      />*/}
              {/*    </td>*/}
              {/*  </tr>*/}
              {/*)}*/}
            </tbody>
          </table>
        </div>
      );
    }
  }

  return (
    <DashboardSection style={{ position: "relative" }}>
      <div className="cal-header-container">
        <SectionHeader
          text={
            <Box
              sx={{
                display: "flex",
                gap: 2,
                alignItems: "center",
              }}
            >
              Events Calendar
              <AddEditEventModal
                canEditEvents={canEditEvents}
                loadDataAsync={loadDataAsync}
              />
            </Box>
          }
          icon={false}
          icontext={
            "The events calendar highlights events that USAID staff are participating in, or that are relevant to the USAID partnering market. We also include some events highlighting partnering opportunities with other international aid donors. Please message us if you are interested in having your event added to our calendar."
          }
        />
        <div className="view-btn-container">
          <div
            className={
              selectedView === "calendar"
                ? "cal-view-btn view-selected"
                : "cal-view-btn"
            }
            onClick={(e) => setSelectedView("calendar")}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17.6281 2.20361H16.7101V3.30543C16.7101 4.21657 15.969 4.95811 15.0577 4.95811C14.1464 4.95811 13.4049 4.21684 13.4049 3.30543V2.20361H11.5687V3.30543C11.5687 4.21657 10.8274 4.95811 9.916 4.95811C9.00486 4.95811 8.26332 4.21684 8.26332 3.30543V2.20361H6.42711V3.30543C6.42711 4.21657 5.68553 4.95811 4.77443 4.95811C3.86329 4.95811 3.12203 4.21684 3.12203 3.30543V2.20361H2.20374C0.988481 2.20361 0 3.19178 0 4.4072V17.7967C0 19.0118 0.988481 20 2.20374 20H17.6282C18.8433 20 19.8321 19.0118 19.8321 17.7967L19.8322 4.4072C19.8322 3.19209 18.8434 2.20377 17.6283 2.20377L17.6281 2.20361ZM18.3627 17.7967C18.3627 18.2014 18.0333 18.5311 17.6281 18.5311H2.20367C1.79879 18.5311 1.46923 18.2016 1.46923 17.7967V7.34482C1.46923 6.93931 1.79816 6.61039 2.20367 6.61039H17.6281C18.0339 6.61039 18.3627 6.939 18.3627 7.34482V17.7967Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M4.77466 4.40705C5.38339 4.40705 5.87648 3.91365 5.87648 3.30524V1.10181C5.87648 0.493407 5.38307 0 4.77466 0C4.16626 0 3.67285 0.493407 3.67285 1.10181V3.3054C3.67285 3.9138 4.16626 4.40705 4.77466 4.40705Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M9.91627 4.40705C10.5247 4.40705 11.0176 3.91365 11.0176 3.30524V1.10181C11.0176 0.493407 10.5247 0 9.91627 0C9.30754 0 8.81445 0.493407 8.81445 1.10181V3.3054C8.81476 3.9138 9.3077 4.40705 9.91627 4.40705Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M15.0575 4.40705C15.666 4.40705 16.1595 3.91365 16.1595 3.30524V1.10181C16.1595 0.493407 15.666 0 15.0575 0C14.4491 0 13.9561 0.493407 13.9561 1.10181V3.3054C13.9561 3.9138 14.4491 4.40705 15.0575 4.40705Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M5.05683 8.15723H3.82732C3.48769 8.15723 3.2124 8.43252 3.2124 8.77214V10.0016C3.2124 10.341 3.4877 10.6166 3.82732 10.6166H5.05683C5.39645 10.6166 5.67174 10.3413 5.67174 10.0016V8.77182C5.67174 8.4322 5.39644 8.15723 5.05683 8.15723Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M8.70673 8.15723H7.47722C7.1376 8.15723 6.8623 8.43252 6.8623 8.77214V10.0016C6.8623 10.341 7.1376 10.6166 7.47722 10.6166H8.70673C9.04635 10.6166 9.32164 10.3413 9.32164 10.0016V8.77182C9.32133 8.4322 9.04619 8.15723 8.70673 8.15723Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M12.3552 8.15723H11.1261C10.7865 8.15723 10.5112 8.43252 10.5112 8.77214V10.0016C10.5112 10.341 10.7864 10.6166 11.1261 10.6166H12.3552C12.695 10.6166 12.9703 10.3413 12.9703 10.0016V8.77182C12.9701 8.4322 12.695 8.15723 12.3552 8.15723Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M16.0046 8.15723H14.7751C14.4358 8.15723 14.1602 8.43252 14.1602 8.77214V10.0016C14.1602 10.341 14.4355 10.6166 14.7751 10.6166H16.0046C16.3442 10.6166 16.6197 10.3413 16.6197 10.0016V8.77182C16.6192 8.4322 16.344 8.15723 16.0046 8.15723Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M5.05683 11.3413H3.82732C3.48769 11.3413 3.2124 11.6166 3.2124 11.9564V13.1857C3.2124 13.525 3.4877 13.8006 3.82732 13.8006H5.05683C5.39645 13.8006 5.67174 13.5254 5.67174 13.1857V11.9564C5.67174 11.6164 5.39644 11.3413 5.05683 11.3413Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M8.70673 11.3413H7.47722C7.1376 11.3413 6.8623 11.6166 6.8623 11.9564V13.1857C6.8623 13.525 7.1376 13.8006 7.47722 13.8006H8.70673C9.04635 13.8006 9.32164 13.5254 9.32164 13.1857V11.9564C9.32133 11.6164 9.04619 11.3413 8.70673 11.3413Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M12.3552 11.3413H11.1261C10.7865 11.3413 10.5112 11.6166 10.5112 11.9564V13.1857C10.5112 13.525 10.7864 13.8006 11.1261 13.8006H12.3552C12.695 13.8006 12.9703 13.5254 12.9703 13.1857V11.9564C12.9701 11.6164 12.695 11.3413 12.3552 11.3413Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M16.0046 11.3413H14.7751C14.4358 11.3413 14.1602 11.6166 14.1602 11.9564V13.1857C14.1602 13.525 14.4355 13.8006 14.7751 13.8006H16.0046C16.3442 13.8006 16.6197 13.5254 16.6197 13.1857V11.9564C16.6192 11.6164 16.344 11.3413 16.0046 11.3413Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M5.05683 14.5249H3.82732C3.48769 14.5249 3.2124 14.8002 3.2124 15.1398V16.3692C3.2124 16.7089 3.4877 16.9842 3.82732 16.9842H5.05683C5.39645 16.9842 5.67174 16.7091 5.67174 16.3692V15.1398C5.67174 14.8005 5.39644 14.5249 5.05683 14.5249Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M8.70673 14.5249H7.47722C7.1376 14.5249 6.8623 14.8002 6.8623 15.1398V16.3692C6.8623 16.7089 7.1376 16.9842 7.47722 16.9842H8.70673C9.04635 16.9842 9.32164 16.7091 9.32164 16.3692L9.32149 15.1398C9.32133 14.8005 9.04619 14.5249 8.70673 14.5249H8.70673Z"
                fill={viewSelectorColors[0]}
              />
              <path
                d="M12.3552 14.5249H11.1261C10.7865 14.5249 10.5112 14.8002 10.5112 15.1398V16.3692C10.5112 16.7089 10.7864 16.9842 11.1261 16.9842H12.3552C12.695 16.9842 12.9703 16.7091 12.9703 16.3692L12.9704 15.1398C12.9701 14.8005 12.695 14.5249 12.3552 14.5249H12.3552Z"
                fill={viewSelectorColors[0]}
              />
            </svg>
          </div>
          <div
            className={
              selectedView === "list"
                ? "list-view-btn view-selected"
                : "list-view-btn"
            }
            onClick={(e) => setSelectedView("list")}
          >
            <svg
              width="18"
              height="14"
              viewBox="0 0 18 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M16.2328 10.8325H6.87909C6.00454 10.8325 5.29541 11.5417 5.29541 12.4162C5.29541 13.2907 6.00454 13.9999 6.87909 13.9999L16.2328 14C17.1074 14 17.8165 13.2909 17.8165 12.4163C17.8165 11.5417 17.1074 10.8325 16.2328 10.8325V10.8325Z"
                fill={viewSelectorColors[1]}
              />
              <path
                d="M1.83112 10.8326H1.58368C0.709134 10.8326 0 11.5418 0 12.4163C0 13.2909 0.709134 14 1.58368 14H1.83112C2.70566 14 3.41479 13.2909 3.41479 12.4163C3.41479 11.5418 2.70579 10.8326 1.83112 10.8326Z"
                fill={viewSelectorColors[1]}
              />
              <path
                d="M16.2328 5.41614H6.87909C6.00454 5.41614 5.29541 6.12528 5.29541 6.99982C5.29541 7.87436 6.00454 8.5835 6.87909 8.5835H16.2328C17.1074 8.5835 17.8165 7.87436 17.8165 6.99982C17.8165 6.12528 17.1074 5.41614 16.2328 5.41614Z"
                fill={viewSelectorColors[1]}
              />
              <path
                d="M1.83112 5.41614H1.58368C0.709134 5.41614 0 6.12528 0 6.99982C0 7.87436 0.709134 8.5835 1.58368 8.5835H1.83112C2.70566 8.5835 3.41479 7.87436 3.41479 6.99982C3.41479 6.12528 2.70579 5.41614 1.83112 5.41614Z"
                fill={viewSelectorColors[1]}
              />
              <path
                d="M16.2328 2.38419e-06H6.87909C6.00454 2.38419e-06 5.29541 0.709136 5.29541 1.58368C5.29541 2.45822 6.00454 3.16736 6.87909 3.16736L16.2328 3.16748C17.1074 3.16748 17.8165 2.45835 17.8165 1.5838C17.8165 0.709133 17.1074 -4.76837e-07 16.2328 -4.76837e-07V2.38419e-06Z"
                fill={viewSelectorColors[1]}
              />
              <path
                d="M1.83112 0.000127077H1.58368C0.709134 0.000127077 0 0.70926 0 1.5838C0 2.45835 0.709134 3.16748 1.58368 3.16748H1.83112C2.70566 3.16748 3.41479 2.45835 3.41479 1.5838C3.41479 0.70926 2.70579 0.000127077 1.83112 0.000127077Z"
                fill={viewSelectorColors[1]}
              />
            </svg>
          </div>
        </div>
      </div>

      {renderView()}
      {/* {selectedEvent? (
      <> */}
      <div
        ref={eventPopupRef}
        onMouseOver={() => {
          setVisibleTooltip(true);
        }}
        style={{ top: bottomOffset ?? "500px" }}
        onMouseLeave={() => {
          setVisibleTooltip(false);
        }}
        className={
          visibleTooltip
            ? "calendar-tooltip visible"
            : "calendar-tooltip hidden"
        }
      >
        <div>
          <p className="calendar-event">{selectedEvent?.name}</p>
        </div>
        <div>
          <p className="event-organization">
            {" "}
            <svg
              style={{ marginRight: "7px" }}
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7.75 4H8.375C8.54076 4 8.69973 3.93415 8.81694 3.81694C8.93415 3.69973 9 3.54076 9 3.375C9 3.20924 8.93415 3.05027 8.81694 2.93306C8.69973 2.81585 8.54076 2.75 8.375 2.75H7.75C7.58424 2.75 7.42527 2.81585 7.30806 2.93306C7.19085 3.05027 7.125 3.20924 7.125 3.375C7.125 3.54076 7.19085 3.69973 7.30806 3.81694C7.42527 3.93415 7.58424 4 7.75 4ZM7.75 6.5H8.375C8.54076 6.5 8.69973 6.43415 8.81694 6.31694C8.93415 6.19973 9 6.04076 9 5.875C9 5.70924 8.93415 5.55027 8.81694 5.43306C8.69973 5.31585 8.54076 5.25 8.375 5.25H7.75C7.58424 5.25 7.42527 5.31585 7.30806 5.43306C7.19085 5.55027 7.125 5.70924 7.125 5.875C7.125 6.04076 7.19085 6.19973 7.30806 6.31694C7.42527 6.43415 7.58424 6.5 7.75 6.5ZM4.625 4H5.25C5.41576 4 5.57473 3.93415 5.69194 3.81694C5.80915 3.69973 5.875 3.54076 5.875 3.375C5.875 3.20924 5.80915 3.05027 5.69194 2.93306C5.57473 2.81585 5.41576 2.75 5.25 2.75H4.625C4.45924 2.75 4.30027 2.81585 4.18306 2.93306C4.06585 3.05027 4 3.20924 4 3.375C4 3.54076 4.06585 3.69973 4.18306 3.81694C4.30027 3.93415 4.45924 4 4.625 4ZM4.625 6.5H5.25C5.41576 6.5 5.57473 6.43415 5.69194 6.31694C5.80915 6.19973 5.875 6.04076 5.875 5.875C5.875 5.70924 5.80915 5.55027 5.69194 5.43306C5.57473 5.31585 5.41576 5.25 5.25 5.25H4.625C4.45924 5.25 4.30027 5.31585 4.18306 5.43306C4.06585 5.55027 4 5.70924 4 5.875C4 6.04076 4.06585 6.19973 4.18306 6.31694C4.30027 6.43415 4.45924 6.5 4.625 6.5ZM12.125 11.5H11.5V0.875C11.5 0.70924 11.4342 0.550268 11.3169 0.433058C11.1997 0.315848 11.0408 0.25 10.875 0.25H2.125C1.95924 0.25 1.80027 0.315848 1.68306 0.433058C1.56585 0.550268 1.5 0.70924 1.5 0.875V11.5H0.875C0.70924 11.5 0.550268 11.5658 0.433058 11.6831C0.315848 11.8003 0.25 11.9592 0.25 12.125C0.25 12.2908 0.315848 12.4497 0.433058 12.5669C0.550268 12.6842 0.70924 12.75 0.875 12.75H12.125C12.2908 12.75 12.4497 12.6842 12.5669 12.5669C12.6842 12.4497 12.75 12.2908 12.75 12.125C12.75 11.9592 12.6842 11.8003 12.5669 11.6831C12.4497 11.5658 12.2908 11.5 12.125 11.5ZM7.125 11.5H5.875V9H7.125V11.5ZM10.25 11.5H8.375V8.375C8.375 8.20924 8.30915 8.05027 8.19194 7.93306C8.07473 7.81585 7.91576 7.75 7.75 7.75H5.25C5.08424 7.75 4.92527 7.81585 4.80806 7.93306C4.69085 8.05027 4.625 8.20924 4.625 8.375V11.5H2.75V1.5H10.25V11.5Z"
                fill="white"
              />
            </svg>
            {selectedEvent?.organization}
          </p>
        </div>
        <div>
          <p className="event-location">
            <svg
              style={{ marginRight: "7px" }}
              width="11"
              height="13"
              viewBox="0 0 11 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.5 0.25C4.17392 0.25 2.90215 0.776784 1.96447 1.71447C1.02678 2.65215 0.5 3.92392 0.5 5.25C0.5 8.625 4.90625 12.4375 5.09375 12.6C5.20696 12.6968 5.35103 12.75 5.5 12.75C5.64897 12.75 5.79304 12.6968 5.90625 12.6C6.125 12.4375 10.5 8.625 10.5 5.25C10.5 3.92392 9.97322 2.65215 9.03553 1.71447C8.09785 0.776784 6.82608 0.25 5.5 0.25ZM5.5 11.2812C4.16875 10.0312 1.75 7.3375 1.75 5.25C1.75 4.25544 2.14509 3.30161 2.84835 2.59835C3.55161 1.89509 4.50544 1.5 5.5 1.5C6.49456 1.5 7.44839 1.89509 8.15165 2.59835C8.85491 3.30161 9.25 4.25544 9.25 5.25C9.25 7.3375 6.83125 10.0375 5.5 11.2812ZM5.5 2.75C5.00555 2.75 4.5222 2.89662 4.11107 3.17133C3.69995 3.44603 3.37952 3.83648 3.1903 4.29329C3.00108 4.75011 2.95157 5.25277 3.04804 5.73773C3.1445 6.22268 3.3826 6.66814 3.73223 7.01777C4.08186 7.3674 4.52732 7.6055 5.01227 7.70196C5.49723 7.79843 5.99989 7.74892 6.45671 7.5597C6.91352 7.37048 7.30397 7.05005 7.57867 6.63893C7.85338 6.2278 8 5.74445 8 5.25C8 4.58696 7.73661 3.95107 7.26777 3.48223C6.79893 3.01339 6.16304 2.75 5.5 2.75ZM5.5 6.5C5.25277 6.5 5.0111 6.42669 4.80554 6.28934C4.59998 6.15199 4.43976 5.95676 4.34515 5.72835C4.25054 5.49995 4.22579 5.24861 4.27402 5.00614C4.32225 4.76366 4.4413 4.54093 4.61612 4.36612C4.79093 4.1913 5.01366 4.07225 5.25614 4.02402C5.49861 3.97579 5.74995 4.00054 5.97835 4.09515C6.20676 4.18976 6.40199 4.34998 6.53934 4.55554C6.67669 4.7611 6.75 5.00277 6.75 5.25C6.75 5.58152 6.6183 5.89946 6.38388 6.13388C6.14946 6.3683 5.83152 6.5 5.5 6.5Z"
                fill="white"
              />
            </svg>
            {selectedEvent?.location}
          </p>
        </div>
        <div>
          <a
            rel="noreferrer"
            className="rsvp-link"
            target="_blank"
            href={selectedEvent?.url}
          >
            <svg
              style={{ marginRight: "7px" }}
              width="13"
              height="13"
              viewBox="0 0 13 13"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M6.56874 8.61875L4.14374 11.0437C3.85014 11.3271 3.45803 11.4855 3.04999 11.4855C2.64195 11.4855 2.24984 11.3271 1.95624 11.0437C1.81224 10.9003 1.69798 10.7299 1.62002 10.5422C1.54206 10.3545 1.50193 10.1532 1.50193 9.95C1.50193 9.74676 1.54206 9.54552 1.62002 9.35782C1.69798 9.17013 1.81224 8.99968 1.95624 8.85625L4.38124 6.43125C4.49893 6.31356 4.56505 6.15394 4.56505 5.9875C4.56505 5.82106 4.49893 5.66144 4.38124 5.54375C4.26355 5.42606 4.10393 5.35994 3.93749 5.35994C3.77105 5.35994 3.61143 5.42606 3.49374 5.54375L1.06874 7.975C0.580212 8.50675 0.316002 9.20667 0.331286 9.92859C0.34657 10.6505 0.640165 11.3386 1.15076 11.8492C1.66135 12.3598 2.34947 12.6534 3.07139 12.6687C3.79332 12.684 4.49324 12.4198 5.02499 11.9312L7.45624 9.50625C7.57393 9.38856 7.64005 9.22894 7.64005 9.0625C7.64005 8.89606 7.57393 8.73644 7.45624 8.61875C7.33855 8.50106 7.17893 8.43494 7.01249 8.43494C6.84605 8.43494 6.68643 8.50106 6.56874 8.61875ZM11.9312 1.06875C11.4055 0.546253 10.6943 0.252991 9.95311 0.252991C9.21188 0.252991 8.50075 0.546253 7.97499 1.06875L5.54374 3.49375C5.48547 3.55202 5.43924 3.6212 5.4077 3.69734C5.37616 3.77348 5.35993 3.85509 5.35993 3.9375C5.35993 4.01991 5.37616 4.10152 5.4077 4.17765C5.43924 4.25379 5.48547 4.32297 5.54374 4.38125C5.60201 4.43952 5.6712 4.48575 5.74733 4.51729C5.82347 4.54882 5.90508 4.56506 5.98749 4.56506C6.0699 4.56506 6.15151 4.54882 6.22765 4.51729C6.30378 4.48575 6.37297 4.43952 6.43124 4.38125L8.85624 1.95625C9.14984 1.67289 9.54195 1.51453 9.94999 1.51453C10.358 1.51453 10.7501 1.67289 11.0437 1.95625C11.1877 2.09968 11.302 2.27013 11.38 2.45782C11.4579 2.64552 11.4981 2.84676 11.4981 3.05C11.4981 3.25324 11.4579 3.45448 11.38 3.64217C11.302 3.82987 11.1877 4.00032 11.0437 4.14375L8.61874 6.56875C8.56016 6.62685 8.51366 6.69598 8.48193 6.77214C8.4502 6.8483 8.43387 6.92999 8.43387 7.0125C8.43387 7.09501 8.4502 7.1767 8.48193 7.25286C8.51366 7.32902 8.56016 7.39815 8.61874 7.45625C8.67684 7.51483 8.74597 7.56133 8.82213 7.59306C8.89829 7.62479 8.97998 7.64112 9.06249 7.64112C9.145 7.64112 9.22669 7.62479 9.30285 7.59306C9.37901 7.56133 9.44814 7.51483 9.50624 7.45625L11.9312 5.025C12.4537 4.49924 12.747 3.78811 12.747 3.04687C12.747 2.30564 12.4537 1.59451 11.9312 1.06875ZM4.51874 8.48125C4.57714 8.53918 4.6464 8.585 4.72255 8.61611C4.7987 8.64721 4.88024 8.66297 4.96249 8.6625C5.04474 8.66297 5.12628 8.64721 5.20243 8.61611C5.27858 8.585 5.34784 8.53918 5.40624 8.48125L8.48124 5.40625C8.59893 5.28856 8.66505 5.12894 8.66505 4.9625C8.66505 4.79606 8.59893 4.63644 8.48124 4.51875C8.36355 4.40106 8.20393 4.33494 8.03749 4.33494C7.87105 4.33494 7.71143 4.40106 7.59374 4.51875L4.51874 7.59375C4.46016 7.65185 4.41366 7.72098 4.38193 7.79714C4.3502 7.8733 4.33387 7.95499 4.33387 8.0375C4.33387 8.12001 4.3502 8.2017 4.38193 8.27786C4.41366 8.35402 4.46016 8.42315 4.51874 8.48125Z"
                fill="white"
              />
            </svg>
            RSVP Link
          </a>
        </div>
      </div>
      {/* </>) :(
        <>
        </>
      )} */}
    </DashboardSection>
  );
};

export default EventsCalendar;
